import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/UseToast";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { login, extractAndStoreDomain, loginOtp } from "@/features/auth/AuthSlice";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { CardContent } from "@/components/ui/Card";
import { IMAGES } from "@/assets/IMAGES";
import { LoginFormSchema, loginSchema, OTPFormValues } from "@/schema/LoginSchema";
import CommonInput from "./CommonInput";
import { useSelector } from "react-redux";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/InputOtp";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { extractLinks } from "@/utils/Helper";
import { useGetCounterByIdQuery, useLazyGetCounterByIdQuery, useUpdateCounterOccupiedStatusMutation } from "@/services/CounterApi";


const {
  VITE_APP_SITEKEY,
  VITE_APP_ENABLE_CAPTCHA,
  VITE_IS_PRODUCTION,
  VITE_IS_LOCAL,
  VITE_LOCAL_URL,
} = import.meta.env;
// Email step schema
const emailSchema = z.object({
  email: z
    .string()
    .min(1, "Required")
    .max(50, "Should be 50 characters or less")
    .regex(
      /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
      "Incorrect email format"
    ),
});

// OTP step schema
const otpSchema = z.object({
  otp: z.string().min(6, "OTP must be 6 digits"),
});

type EmailFormValues = z.infer<typeof emailSchema>;
type OtpFormValues = z.infer<typeof otpSchema>;

interface SwitchUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SwitchUserDialog({ open, onOpenChange }: SwitchUserDialogProps) {
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const [isCaptchaError, setCaptchaError] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null); // Reference to reCAPTCHA
  const dispatch = useDispatch<AppDispatch>();
  const [isVerifying, setIsVerifying] = useState(false); // Add state for verifying OTP
  const [isCounterOpen, setIsCounterOpen] = React.useState(
      localStorage.getItem("counter_open") === "true"
    );
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const { toast } = useToast();
    const { loading, error, isEmailVerify } = useSelector(
      (state: RootState) => state.auth
    );
  
  const { data } = useGetCounterByIdQuery(userInfo?.counter_id || 0);
  const [updateCounterStatus, { isLoading:isCounterLoading }] =
      useUpdateCounterOccupiedStatusMutation();


    useEffect(() => {
      if (data) {
        const isOccupied = data.is_occupied;
        const occupiedBy = data?.occupied_by;
        const currentUser = userInfo?.id;

        // Counter should be open only if occupiedBy matches current user
        const shouldOpen = isOccupied && occupiedBy === currentUser;

        setIsCounterOpen(shouldOpen);
        localStorage.setItem("counter_open", shouldOpen?.toString());
        localStorage.setItem("is_occupied", isOccupied?.toString());
      }
    }, [data, userInfo?.id]);


  const navigate = useNavigate();
    const {
      control,
      handleSubmit,
      setValue,
      formState: { errors },
      reset:loginReset,
    } = useForm<LoginFormSchema>({
      resolver: zodResolver(loginSchema),
      defaultValues: {
        email: email,
        rememberMe: false,
      },
    });


      const {
        control: otpControl,
        handleSubmit: otpHandleSubmit,
        watch,
        trigger,
        formState: { errors : otpErrors },
        reset,
      } = useForm<OTPFormValues>({
        resolver: zodResolver(otpSchema),
        defaultValues: { otp: "" },
      });
  // Email form
  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });
  
  // OTP form
  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: "" },
  });
  const [remainingTime, setRemainingTime] = useState<number>(
0    
  );
  
  const [triggerGetCounter, { data: counterData }] =
    useLazyGetCounterByIdQuery();


  //Handle OTP submission
  const onOtpSubmit = async (data: OtpFormValues) => {
    setIsVerifying(true);
  
    if (data.otp) {
      // Check if current user has an occupied counter that needs to be closed
      if (data.otp && userInfo?.counter_id) {
        // Only call if counter_id is valid (not null, undefined, or 0)
        if (userInfo.counter_id !== null && userInfo.counter_id !== undefined && userInfo.counter_id !== 0) {
          const currentCounterData = await triggerGetCounter(userInfo.counter_id).unwrap();
          const isCurrentCounterOccupied = currentCounterData?.is_occupied;
          const isOccupiedByCurrentUser = currentCounterData?.occupied_by === userInfo?.id;

          if (
            isCurrentCounterOccupied &&
            isOccupiedByCurrentUser &&
            userInfo?.counter_id &&
            userInfo?.id
          ) {
            try {
              await updateCounterStatus({
                id: userInfo.counter_id,
                staff_id: userInfo.id,
              }).unwrap();
              // Update local storage to reflect counter is closed
              localStorage.setItem("is_occupied", "false");
              localStorage.setItem("counter_open", "false");
            } catch (error) {
              console.error("Failed to close counter during user switch:", error);
            }
          }
        }
      }

      dispatch(
        loginOtp({
          email,
          otp: data.otp,
          fcm_token: "",
          its_id: "",
          domain:
            VITE_IS_LOCAL === "true"
              ? VITE_LOCAL_URL
              : VITE_IS_PRODUCTION === "true"
                ? window.location.hostname
                : window.location.host,
        })
      )
        .unwrap()
        .then((response) => {
          localStorage.removeItem("userSwitchInProgress");
          toast({
            variant: "success",
            description: (
              <div className="flex items-center space-x-2 w-full h-10">
                <img
                  src={IMAGES.Status_icon}
                  alt="Success"
                  className="w-6 h-6"
                />
                <span>{response.message}</span>
              </div>
            ),
          });

          // Close the dialog
          onOpenChange(false);

          // Get the updated side panel data
          const sidepanel = localStorage.getItem("sidePanel");
          const decodedSidepanel = JSON.parse(atob(sidepanel as string));
          const links = extractLinks(decodedSidepanel);

          // Dispatch an event to notify other components of user change
          window.dispatchEvent(
            new CustomEvent("userSwitched", {
              detail: { sidepanel: decodedSidepanel },
            })
          );

          // Get current location
          const currentPath = window.location.pathname;

          // Check if the current path is in the available links
          const hasPermissionForCurrentPage = links.some(
            (link) => currentPath === link || currentPath.startsWith(link + "/")
          );

          // Only navigate if user doesn't have permission for current page
          if (!hasPermissionForCurrentPage && links && links.length > 0) {
            navigate(links[0], { replace: true });
          }

          setStep("email");
          reset({ otp: "" });
          loginReset({ email: "" });
          setCaptchaValue(null);
        })
        .catch((error) => {
          toast({
            variant: "destructive",
            description: error,
          });
        })
        .finally(() => {
          setIsVerifying(false);
        });
    }
  };

  useEffect(() => {
    // Start countdown timer when otp_expiry_seconds is available
    if (remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }, 1000);

      // Clear interval when the component is unmounted or remainingTime reaches 0
      return () => clearInterval(timer);
    }
  }, [remainingTime]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Always allow the dialog to close and reset the state
    localStorage.removeItem("userSwitchInProgress");
    setStep("email");
     reset({ otp: "" });
     loginReset({ email: "" });
      setCaptchaValue(null);
    }
    onOpenChange(open);
  };
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

    const onSubmit = async (data: LoginFormSchema) => {
      const enableCaptcha = VITE_APP_ENABLE_CAPTCHA !== "false"; // Default to true if not specified
  
      if (enableCaptcha) {
        const recaptchaValue = recaptchaRef.current?.getValue();
        if (!recaptchaValue) {
          setCaptchaError(true);
          toast({
            variant: "destructive",
            description: "Please complete the CAPTCHA to proceed.",
          });
          return;
        }
      }

      //set a flag to indicate that the user is logging in
      localStorage.setItem("userSwitchInProgress", "true");  
      extractAndStoreDomain(window.location.origin);    // Dispatch the login thunk
      dispatch(
        login({
          email: data.email.toLocaleLowerCase(),
          rememberme: data.rememberMe ?? false,
          domain:
            VITE_IS_LOCAL === "true"
              ? VITE_LOCAL_URL
              : VITE_IS_PRODUCTION === "true"
                ? window.location.hostname
                : window.location.host,
        })
      )
        .unwrap()
        .then((response) => {
          toast({
            variant: "success",
            description: (
              <div className="flex items-center space-x-2 w-full h-10">
                <img src={IMAGES.Status_icon} alt="Success" className="w-6 h-6" />
                <span>{response.message}</span>
              </div>
            ),
          });
          // Instead of navigating away, set the email and move to OTP step
          setEmail(data.email);
          setStep("otp");
          if (response?.otp_expiry_seconds) {
            setRemainingTime(response.otp_expiry_seconds);
          }
        })
        .catch((error) => {
          toast({
            variant: "destructive",
            description: error,
          });
        });
    };





 const onRecaptchaChange = (value: any) => {
   setCaptchaError(false);
 };
   const isEmailinLocal = localStorage.getItem("email") ?? "";

   const onResentOtp = async () => {
     dispatch(
       login({
         email,
         rememberme:false,
         domain:
           VITE_IS_LOCAL === "true"
             ? VITE_LOCAL_URL
             : VITE_IS_PRODUCTION === "true"
               ? window.location.hostname
               : window.location.host,
       })
     )
       .unwrap()
       .then(({ message, ...response }) => {
         toast({
           variant: "success",
           description: (
             <div className="flex items-center space-x-2 w-full h-10">
               <img src={IMAGES.Status_icon} alt="Success" className="w-6 h-6" />
               <span>{message}</span>
             </div>
           ),
         });
 
         if (response?.otp_expiry_seconds) {
           setRemainingTime(response.otp_expiry_seconds);
         }
         reset({ otp: "" }); // Reset the OTP field
       })
       .catch((error) => {
         toast({
           variant: "destructive",
           description: error,
         });
       });
   };

  return (
    <Dialog open={open} modal={true}>
      <DialogContent
       hideCloseButton={true}
        className="sm:max-w-[425px] overflow-visible z-50"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold text-primary">
            Switch User
          </DialogTitle>
          <DialogDescription className="text-center">
            {step === "email"
              ? "Enter email to receive an OTP"
              : "Enter the OTP sent to your email"}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <Controller
                name="email"
                rules={{
                  required: "Required",
                  maxLength: {
                    value: 50,
                    message: "Should be 50 characters or less",
                  },
                  pattern: {
                    value:
                      /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                    message: "Incorrect email format",
                  },
                }}
                control={control}
                render={({ field }) => (
                  <CommonInput
                    {...field}
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    error={errors.email?.message}
                    isIconAllow={true}
                    className="w-full !text-black"
                  />
                )}
              />
            </div>

            {/* Conditionally render reCAPTCHA */}
            {VITE_APP_ENABLE_CAPTCHA && (
              <ReCAPTCHA
                ref={recaptchaRef}
                theme="dark"
                sitekey={VITE_APP_SITEKEY}
                onChange={onRecaptchaChange}
                size="normal"
              />
            )}

            {isCaptchaError && (
              <>
                <span className="text-red-400 text-xs font-poppins">
                  Please complete the reCaptcha
                </span>
              </>
            )}

            <LoadingButton
              loading={loading}
              type="submit"
              className="!w-full hover:bg-secondary text-black"
            >
              Login
            </LoadingButton>
          </form>
        ) : (
          <CardContent>
            <form className="space-y-4" onSubmit={otpHandleSubmit(onOtpSubmit)}>
              <div className="flex my-3 flex-col justify-center items-center">
                <Controller
                  name="otp"
                  control={otpControl}
                  render={({ field }) => (
                    <InputOTP
                      maxLength={6}
                      {...field}
                      onInput={(e) => {
                        // Remove non-numeric characters
                        e.currentTarget.value = e.currentTarget.value.replace(
                          /\D/g,
                          ""
                        );
                      }}
                    >
                      <InputOTPGroup>
                        {[...Array(6)].map((_, index) => (
                          <InputOTPSlot key={index} index={index} />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  )}
                />
                {otpErrors.otp && (
                  <p className="text-red-500 text-sm mt-2 w-full px-14">
                    {otpErrors.otp.message}
                  </p>
                )}
                <div className="flex justify-between items-center w-full mt-3 ">
                  <p className="text-[.8rem] text-primary">
                    Remaining time:{" "}
                    <span className="text-textgray">
                      {formatTime(remainingTime)}
                    </span>
                  </p>
                  <TooltipProvider>
                    {remainingTime < 90 ? (
                      <button
                        onClick={onResentOtp}
                        disabled={remainingTime >= 90}
                        className="disabled:cursor-not-allowed text-[.8rem] text-primary disabled:text-gray-400 underline hover:cursor-pointer"
                      >
                        Resend OTP
                      </button>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger
                          asChild
                          className="hover:cursor-pointer"
                        >
                          <button
                            onClick={onResentOtp}
                            disabled={remainingTime >= 90}
                            className="disabled:cursor-not-allowed text-[.8rem] text-primary disabled:text-gray-400 underline hover:cursor-pointer"
                          >
                            Resend OTP
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-52">
                          You can request a new OTP 30 seconds after the last
                          OTP request.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TooltipProvider>
                </div>
              </div>
              {/* <CommonButton title="Continue" type="submit" /> */}

              <Button
                type="submit"
                disabled={isVerifying}
                className="w-full text-black  hover:text-white hover:bg-primary"
              >
                {isVerifying ? "Verifying OTP" : "Verify OTP & Continue"}
                {isVerifying && (
                  <div className="w-5 h-5 ml-2 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                )}
              </Button>
            </form>
          </CardContent>
        )}
      </DialogContent>
    </Dialog>
  );
}